import { StepperSelectionEvent } from '@angular/cdk/stepper';
import { Component, OnDestroy, OnInit, Signal, ViewChild, WritableSignal, computed, effect, inject, signal } from '@angular/core'; // Added computed
import { FormBuilder } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatStepper } from '@angular/material/stepper';
import { Router } from '@angular/router';
import { CreatePriceRequest, LocalizedString } from '@hjc/masterdata-types';
import { TranslateService } from '@ngx-translate/core';
import { ErrorService } from '@services/error.service';
import { IntegrationConfig, IntegrationManagerService, PrimaryIntegrationData } from '@services/integration-manager.service';
import { NotificationService } from '@services/notification.service';
import { PricingService } from '@services/pricing.service';
import { StepperNavigationService } from '@services/stepper-navigation.service';
import { WebSocketService } from '@services/websocket/websocket.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AddPriceComponent, AddPriceData, AddPriceItem, AddPriceResult } from '../pricing/components/add-price/add-price.component';

export type BaseIntegrationSystemStatus = 'pending' | 'in-progress' | 'completed' | 'failed';

export type BaseIntegrationSystem = {
  name: string;
  status: BaseIntegrationSystemStatus;
  statusText: string;
  error?: boolean;
};

// Constants for step indices and delays
const FIRST_UI_STEP = 0;
const INTEGRATIONS_STEP = 1;
const STEP_1_DELAY_MS = 3000;
const SAVE_SUCCESS_DELAY_MS = 1500;

@Component({
  template: ''
})
export abstract class BasePartnerCreationComponent<T> implements OnInit, OnDestroy {
  protected errorService = inject(ErrorService);
  protected notificationService = inject(NotificationService);
  protected webSocketService = inject(WebSocketService);
  protected pricingService = inject(PricingService);
  protected formBuilder = inject(FormBuilder);
  protected dialog = inject(MatDialog);
  protected translateService = inject(TranslateService);
  protected stepperNavigation = inject(StepperNavigationService);
  protected integrationManager = inject(IntegrationManagerService);
  protected router = inject(Router);

  protected destroy$ = new Subject<void>();

  @ViewChild(MatStepper) public stepper!: MatStepper;

  protected createdItem: T | null = null;
  protected primaryItemData: PrimaryIntegrationData | null = null;
  protected priceDataForChild: WritableSignal<AddPriceData<AddPriceItem> | undefined> = signal(undefined);
  protected priceListSignal: WritableSignal<AddPriceResult[]> = signal([]);
  protected isLoading: WritableSignal<boolean> = signal(false);

  public priceData: Signal<AddPriceData<AddPriceItem> | undefined> = this.priceDataForChild.asReadonly();
  public createdItemPrices: Signal<AddPriceResult[]> = this.priceListSignal.asReadonly();

  private lastNavigatedStepDueToCompletion = -1; // Add this to prevent re-triggering navigation for the same completed step

  public isWebSocketConnected: Signal<boolean>;
  public currentLang: Signal<string>;
  public integrationStatuses!: Signal<readonly BaseIntegrationSystem[]>;
  public currentStepCompletionStatus: Signal<readonly boolean[]>;

  // Abstract methods
  protected abstract fetchDataForPricing(): Promise<void>;
  protected abstract createPriceObject(input: AddPriceResult): CreatePriceRequest | undefined;
  protected abstract getPartnerIntegrationConfigs(): IntegrationConfig[];
  protected abstract getTotalSteps(): number;
  protected abstract getDetailsRoute(partnerId: string): string[];

  protected getItemNameLocalized(name: string | LocalizedString | undefined | null): LocalizedString {
    if (!name) {
      return { fi: '', en: '', sv: '' };
    }
    return typeof name === 'string' ? { fi: name, en: name, sv: name } : name;
  }

  constructor() {
    this.isWebSocketConnected = computed(() => this.webSocketService.isConnected());
    this.currentLang = signal(this.translateService.currentLang);
    this.currentStepCompletionStatus = this.stepperNavigation.stepsCompletion;

    this.setupAutomaticStepProgression();
  }

  private setupAutomaticStepProgression(): void {
    // Effect for automatic step progression
    effect(() => {
      const stepsCompletionArray = this.stepperNavigation.stepsCompletion();
      const currentStepperIndex = this.stepper ? this.stepper.selectedIndex : -1;
      const primaryId = this.integrationManager.primaryItemId()();

      if (currentStepperIndex === -1 || currentStepperIndex >= this.getTotalSteps() - 1) {
        return; // Stepper not ready or already on the last step
      }

      const isCurrentUIStepCompleted = stepsCompletionArray[currentStepperIndex];

      // Check if the *current visible step* in the stepper has just been marked as completed
      // And if we haven't already auto-navigated away from this completed step
      if (isCurrentUIStepCompleted && this.lastNavigatedStepDueToCompletion !== currentStepperIndex) {
        this.handleStepCompletion(currentStepperIndex, primaryId);
      }
    });
  }

  private handleStepCompletion(currentStepperIndex: number, primaryId: string | undefined): void {
    // For step 1, ensure we have a primaryId before proceeding
    if (currentStepperIndex === FIRST_UI_STEP) {
      if (primaryId) {
        this.lastNavigatedStepDueToCompletion = currentStepperIndex;
        this.safelyMoveNextStepWithDelay();
      }
      return;
    }

    this.lastNavigatedStepDueToCompletion = currentStepperIndex;
    const delayMs = currentStepperIndex === INTEGRATIONS_STEP ? STEP_1_DELAY_MS : 0;
    this.safelyMoveNextStepWithDelay(delayMs);
  }

  ngOnInit(): void {
    this.integrationManager.prepareForNewCreationFlow();
    this.stepperNavigation.initializeStepper(this.getTotalSteps());
    this.integrationStatuses = this.integrationManager.getIntegrationSystems();
    if (!this.webSocketService.isConnected()) {
      this.connectWebSocket();
    }

    this.startStepIntegrationsWhenReady();
  }

  private stepIntegrationSignalsCache = new Map<number, Signal<readonly BaseIntegrationSystem[]>>();

  /**
   * Get integrations filtered by the current step
   * @param stepNumber The step number (1-indexed)
   * @returns Signal of integration systems for the specified step
   */
  public getIntegrationsForStep(stepNumber: number): Signal<readonly BaseIntegrationSystem[]> {
    if (this.stepIntegrationSignalsCache.has(stepNumber)) {
      return this.stepIntegrationSignalsCache.get(stepNumber)!;
    }

    const signal = computed(() => {
      const allIntegrations = this.integrationStatuses();
      const configs = this.getPartnerIntegrationConfigs();
      const stepIntegrationNames = configs.filter(config => config.step === stepNumber).map(config => config.name);
      const filtered = allIntegrations.filter(integration => stepIntegrationNames.includes(integration.name));

      return filtered;
    });

    this.stepIntegrationSignalsCache.set(stepNumber, signal);
    return signal;
  }

  protected connectWebSocket(): void {
    this.webSocketService.connect();
  }

  protected disconnectWebSocket(): void {
    this.webSocketService.disconnect();
  }

  private safelyMoveNextStepWithDelay(delayMs = 0): void {
    if (this.stepper && this.stepper.selectedIndex < this.getTotalSteps() - 1) {
      setTimeout(() => {
        // Re-check condition inside timeout as state might have changed
        if (this.stepper && this.stepper.selectedIndex < this.getTotalSteps() - 1) {
          this.stepperNavigation.moveToNextStep(this.stepper);

          // If we've just moved into the final step, schedule cleanup after the UI has stabilized
          const newIndex = this.stepper.selectedIndex;
          const lastIndex = this.getTotalSteps() - 1;
          if (newIndex === lastIndex) {
            // Note: Cleanup is now handled in onSaveDone when the user confirms completion
            // This prevents premature cleanup if user navigates back in the stepper
          }
        }
      }, delayMs);
    }
  }

  private startStepIntegrationsWhenReady(): void {
    if (this.webSocketService.isConnected()) {
      this.setupIntegrationCompletionListeners(1);
    } else {
      const connectionSub = this.webSocketService
        .getConnectionStatus()
        .pipe(takeUntil(this.destroy$))
        .subscribe(connected => {
          if (connected) {
            this.setupIntegrationCompletionListeners(1);
            connectionSub.unsubscribe();
          }
        });
    }
  }

  /**
   * Unified entry to set up listeners for integration completion notifications.
   * If `stepNumber` is omitted the method will attempt to set up listeners for the current UI step.
   */
  protected setupIntegrationCompletionListeners(stepNumber: number, primaryItemId?: string): void {
    this.isLoading.set(true);
    const configs = this.getPartnerIntegrationConfigs();
    // Pre-subscribe to all topics at creation start to avoid race where backend
    // emits before client's subscription record is persisted.
    if (stepNumber === 1) {
      try {
        this.integrationManager.preSubscribeAllTopics(configs);
      } catch (err) {
        console.warn('[Base] preSubscribeAllTopics failed', err);
      }
    }

    this.integrationManager.setupIntegrationCompletionListenersForStep(
      configs,
      primaryItemId,
      stepNumber,
      stepNumber === 1 ? this.handleStep1Data.bind(this) : undefined
    );
  }

  private handleStep1Data(primaryData: PrimaryIntegrationData): void {
    this.primaryItemData = primaryData;
    this.isLoading.set(false);
  }

  protected onStepChange(event: StepperSelectionEvent): void {
    const newSelectedStepperIndex = event.selectedIndex;
    const newLogicalStep = newSelectedStepperIndex + 1;

    if (!this.stepperNavigation.onStepChange(event)) {
      return;
    }
    if (event.previouslySelectedIndex !== this.lastNavigatedStepDueToCompletion) {
      this.lastNavigatedStepDueToCompletion = -1;
    }

    const totalUiSteps = this.stepperNavigation.totalSteps();
    if (newSelectedStepperIndex === totalUiSteps - 1 && !this.priceDataForChild()) {
      this.fetchDataForPricing();
    }

    this.handleIntegrationRestartForStep(newSelectedStepperIndex, newLogicalStep);
  }

  private handleIntegrationRestartForStep(uiStepIndex: number, logicalStep: number): void {
    if (uiStepIndex === FIRST_UI_STEP) {
      const { allProcessed, anyInProgress } = this.getStepIntegrationStatus(INTEGRATIONS_STEP);
      if (!allProcessed && !anyInProgress) {
        this.setupIntegrationCompletionListeners(INTEGRATIONS_STEP, undefined);
      }
    } else if (uiStepIndex > FIRST_UI_STEP) {
      const primaryIdForStep = this.primaryItemData?.id ?? this.integrationManager.primaryItemId()();
      if (primaryIdForStep) {
        const { allProcessed, anyInProgress } = this.getStepIntegrationStatus(logicalStep);
        if (!allProcessed && !anyInProgress) {
          this.setupIntegrationCompletionListeners(logicalStep, primaryIdForStep);
        }
      } else {
        console.error(
          `Base: onStepChange - Cannot set up integration completion listeners for UI step index ${uiStepIndex} (Logical step ${logicalStep}) as primary item ID (from primary item data) is missing.`
        );
        this.errorService.showError(this.translateService.instant('CREATE.ERROR_DATA_MISSING_FOR_STEP', { step: logicalStep }));
      }
    }
  }

  private getStepIntegrationStatus(stepNumber: number): { allProcessed: boolean; anyInProgress: boolean } {
    const stepConfigs = this.getPartnerIntegrationConfigs().filter(c => c.step === stepNumber);
    const stepIntegrations = this.integrationStatuses().filter(s => stepConfigs.some(sc => sc.name === s.name));
    const allProcessed = stepIntegrations.every(s => s.status === 'completed' || s.status === 'failed');
    const anyInProgress = stepIntegrations.some(s => s.status === 'in-progress');

    return { allProcessed, anyInProgress };
  }

  public onPriceAdded(price: AddPriceResult): void {
    const cloned: AddPriceResult = {
      ...price,
      itemName: price.itemName ? { ...price.itemName } : { fi: '', en: '', sv: '' },
      validFrom: price.validFrom ? new Date(price.validFrom.getTime()) : new Date()
    };

    this.priceListSignal.update((prices: AddPriceResult[]) => [...prices, cloned]);
  }

  public onEditPrice(price: AddPriceResult): void {
    const dialogRef = this.openEditPriceDialog(price);

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.updatePriceInList(result);
        this.savePrice(result);
      }
    });
  }

  private openEditPriceDialog(price: AddPriceResult) {
    return this.dialog.open(AddPriceComponent, {
      width: '600px',
      data: {
        ...(this.priceDataForChild || {}),
        ...price,
        isEdit: true
      }
    });
  }

  private updatePriceInList(updatedPrice: AddPriceResult): void {
    this.priceListSignal.update((prices: AddPriceResult[]) => {
      const index = prices.findIndex(p => p.itemId === updatedPrice.itemId && p.validFrom.toISOString() === updatedPrice.validFrom.toISOString());
      if (index !== -1) {
        prices[index] = updatedPrice;
        return [...prices];
      }
      return prices;
    });
  }

  public async onSaveDone(): Promise<void> {
    this.isLoading.set(true);
    try {
      await this.saveAllPrices();
      this.showSaveSuccessNotification();
      await this.handlePostSaveCleanupAndNavigation();
    } catch (error) {
      console.error(error);
      this.errorService.showError(this.translateService.instant('NOTIFICATIONS.ERROR.PRICE_SAVE_FAILED'));
    } finally {
      this.isLoading.set(false);
    }
  }

  private async saveAllPrices(): Promise<void> {
    for (const price of this.priceListSignal()) {
      await this.savePrice(price);
    }
  }

  private showSaveSuccessNotification(): void {
    this.notificationService.success(this.translateService.instant('NOTIFICATIONS.SUCCESS.ALL_PRICES_SAVED'));
  }

  private async handlePostSaveCleanupAndNavigation(): Promise<void> {
    const primaryId = this.primaryItemData?.id ?? this.integrationManager.primaryItemId()();
    if (primaryId) {
      await this.cleanupIntegrationSubscriptions();
      this.navigateToDetailsWithDelay(primaryId);
    } else {
      console.warn('No primary item ID available for navigation, staying on current page');
    }
  }

  private async cleanupIntegrationSubscriptions(): Promise<void> {
    try {
      this.integrationManager.cleanupAllSubscriptions();
    } catch (cleanupErr) {
      console.warn('Failed to cleanup integration subscriptions after Save Done:', cleanupErr);
    }
  }

  private navigateToDetailsWithDelay(primaryId: string): void {
    const detailsRoute = this.getDetailsRoute(primaryId);
    setTimeout(() => {
      this.router.navigate(detailsRoute);
    }, SAVE_SUCCESS_DELAY_MS);
  }

  async savePrice(price: AddPriceResult): Promise<void> {
    const priceObj = this.createPriceObject(price);
    if (!priceObj) {
      this.errorService.showError(this.translateService.instant('NOTIFICATIONS.ERROR.CREATE_PRICE_OBJECT_FAILED'));
      return;
    }
    try {
      await this.pricingService.addPrice(priceObj);
      this.notificationService.success(this.translateService.instant('NOTIFICATIONS.SUCCESS.PRICE_SAVED'));
    } catch (error) {
      console.error('Error saving price:', error);
      const errorMessage = error instanceof Error ? error.message : String(error);
      this.errorService.showError(this.translateService.instant('NOTIFICATIONS.ERROR.ERROR_SAVING_PRICE', { error: errorMessage }));
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.webSocketService.isConnected()) {
      this.disconnectWebSocket();
    }
    this.stepIntegrationSignalsCache.clear();
  }
}
